import { useState } from 'react';
import type { InsuranceProviderPlanDetailViewModel } from '@generated';
import { editPlanCodesOptions, getInsuranceProviderPlanDetailOptions } from '@generated';
import { useMutation } from '~/util/http/useMutation';
import { Plus, X } from 'lucide-react';

interface CodesTabProps {
	plan: InsuranceProviderPlanDetailViewModel;
}

export function CodesTab({ plan }: CodesTabProps) {
	const [typeOfBillCodes, setTypeOfBillCodes] = useState<string[]>(plan.insuranceProviderCodes?.typeOfBillCodes ?? []);
	const [revenueCodes, setRevenueCodes] = useState<string[]>(plan.insuranceProviderCodes?.revenueCodes ?? []);
	const [hcpcsCodes, setHcpcsCodes] = useState<string[]>(plan.insuranceProviderCodes?.hcpcsCodes ?? []);
	const [conditionCodes, setConditionCodes] = useState<string[]>(plan.insuranceProviderCodes?.conditionCodes ?? []);

	const saveMutation = useMutation({
		...editPlanCodesOptions,
		invalidateQueryKeys: [getInsuranceProviderPlanDetailOptions(plan.id).queryKey],
		onSuccessMessage: 'Codes updated successfully'
	});

	const handleSave = async () => {
		await saveMutation.mutateAsync({
			planId: plan.id,
			typeOfBillCodes,
			revenueCodes,
			hcpcsCodes,
			conditionCodes
		});
	};

	return (
		<div className="mt-6 space-y-6">
			<CodeListEditor title="Type of Bill Codes" codes={typeOfBillCodes} onChange={setTypeOfBillCodes} placeholder="e.g., 0322" />
			<CodeListEditor title="Revenue Codes" codes={revenueCodes} onChange={setRevenueCodes} placeholder="e.g., 0551" />
			<CodeListEditor title="HCPCS Codes" codes={hcpcsCodes} onChange={setHcpcsCodes} placeholder="e.g., G0151" />
			<CodeListEditor title="Condition Codes" codes={conditionCodes} onChange={setConditionCodes} placeholder="e.g., 07" />

			<div className="flex justify-end border-t border-gray-200 pt-4">
				<button
					type="button"
					onClick={handleSave}
					disabled={saveMutation.isPending}
					className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
				>
					{saveMutation.isPending ? 'Saving...' : 'Save Codes'}
				</button>
			</div>
		</div>
	);
}

function CodeListEditor({
	title,
	codes,
	onChange,
	placeholder
}: {
	title: string;
	codes: string[];
	onChange: (codes: string[]) => void;
	placeholder: string;
}) {
	const [newCode, setNewCode] = useState('');

	const addCode = () => {
		const trimmed = newCode.trim();
		if (trimmed && !codes.includes(trimmed)) {
			onChange([...codes, trimmed]);
			setNewCode('');
		}
	};

	const removeCode = (code: string) => {
		onChange(codes.filter(c => c !== code));
	};

	return (
		<div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
			<h4 className="mb-3 text-sm font-semibold text-gray-700">{title}</h4>
			<div className="mb-3 flex gap-2">
				<input
					type="text"
					value={newCode}
					onChange={e => setNewCode(e.target.value)}
					onKeyDown={e => e.key === 'Enter' && addCode()}
					placeholder={placeholder}
					className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
				/>
				<button
					type="button"
					onClick={addCode}
					className="inline-flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200"
				>
					<Plus className="size-3" />
					Add
				</button>
			</div>
			{codes.length === 0 ? (
				<p className="text-xs text-gray-400">No codes configured</p>
			) : (
				<div className="flex flex-wrap gap-2">
					{codes.map(code => (
						<span key={code} className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700">
							{code}
							<button type="button" onClick={() => removeCode(code)} className="text-gray-400 hover:text-gray-600">
								<X className="size-3" />
							</button>
						</span>
					))}
				</div>
			)}
		</div>
	);
}
